import React, { memo, useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { IconButton, InputLabel, TextField } from '@picsio/ui';
import { Gear } from '@picsio/icons';

import localization from '../../../shared/strings';
import Tooltip from '../../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import { useLocalFieldValue } from '../hooks';
import { productFieldInputProps } from '../constants';
import ProductTypesManagementDialog from '../ProductTypesManagementDialog';
import parentStyles from '../styles.module.scss';
import ProductTypesDropdown from './productTypesDropdown';
import styles from './ProductType.module.scss';

const l10n = localization.PRODUCT_INFO.productType;

const ProductType = ({
  value, readOnly, readOnlyTooltip, tooltip, isDiff, disabled, onChange,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const showManageButton = useSelector(
    (state) => state.productTypes.isLoaded && state.productTypes.items.length > 0,
  );

  const handleChange = useCallback((e) => {
    setLocalValue(e.target.value);
  }, [setLocalValue]);

  const handleBlur = useCallback(() => {
    const trimmed = localValue.trim();
    if (trimmed !== localValue) setLocalValue(trimmed);
    if (trimmed !== value) onChange?.(trimmed);
  }, [localValue, value, setLocalValue, onChange]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
  }, []);

  const handleSelect = useCallback((productType) => {
    onChange?.(productType.title);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange?.('');
  }, [onChange]);

  const handleOpenManageDialog = useCallback(() => {
    setIsManageDialogOpen(true);
  }, []);

  const handleCloseManageDialog = useCallback(() => {
    setIsManageDialogOpen(false);
  }, []);

  const label = (
    <Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top">
      <span className={isDiff ? parentStyles.diffDot : undefined}>{l10n.label}</span>
    </Tooltip>
  );

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip || (readOnly ? readOnlyTooltip : null)}>
      <Choose>
        <When condition={readOnly}>
          <div className={parentStyles.row}>
            <TextField
              value={localValue}
              disabled={readOnly}
              label={label}
              inputProps={productFieldInputProps}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
            />
          </div>
        </When>
        <Otherwise>
          <div className={styles.wrapper}>
            <InputLabel position="top" margin>
              {label}
            </InputLabel>
            <div className={styles.fieldRow}>
              <ProductTypesDropdown
                value={value}
                disabled={disabled}
                onSelect={handleSelect}
                onClear={handleClear}
              />
              <If condition={showManageButton}>
                <Tooltip content={l10n.manageTitle} placement="top">
                  <IconButton size="lg" disabled={disabled} onClick={handleOpenManageDialog}>
                    <Gear />
                  </IconButton>
                </Tooltip>
              </If>
            </div>
            <If condition={isManageDialogOpen}>
              <ProductTypesManagementDialog onClose={handleCloseManageDialog} />
            </If>
          </div>
        </Otherwise>
      </Choose>
    </ReadOnlyFieldTooltip>
  );
};

ProductType.propTypes = {
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  tooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

ProductType.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
  tooltip: null,
  isDiff: false,
  disabled: false,
  onChange: null,
};

export default memo(ProductType);
